/**
 * Imputa las partidas ejecutadas (leídas del archivo del mercado) contra las
 * cauciones que el comercial ya cargó en la planilla, y clasifica el resultado
 * para que el trader vea de una lo que quedó sin operar o operado a medias.
 *
 * Es una función pura: recibe partidas y cauciones, devuelve el plan de
 * escritura. Quien persiste es `caucionesImportService`.
 */

export const ESTADO_COMPLETA = 'Completa';
export const ESTADO_PARCIAL = 'Parcial';
export const ESTADO_EXCEDIDA = 'Excedida';
export const ESTADO_NO_OPERADA = 'NoOperada';

export const MOTIVO_SIN_PEDIDO = 'SinPedido';
export const MOTIVO_CARTERA_PROPIA = 'CarteraPropia';
export const MOTIVO_SIN_COMITENTE = 'SinComitente';

/**
 * Estado de conciliación de una partida que coincide con una caución que el
 * trader ya cerró a mano: queda registrada contra esa caución pero no le
 * escribe parcial ni la modifica.
 */
export const CONCILIACION_YA_CARGADA = 'YaCargada';

/** Estados de una caución que todavía admite imputación. */
export const ESTADOS_ABIERTOS = ['Pendiente', 'En Proceso'];

/** Estados en los que una caución sigue vigente con lo que ya se le imputó. */
const ESTADOS_VIGENTES = [...ESTADOS_ABIERTOS, 'Hecha'];

/**
 * Tolerancia relativa para dar una caución por completa.
 *
 * El monto que carga el comercial casi nunca coincide al peso con lo que se
 * ejecuta: el trader cubre el descubierto exacto del cliente, que se mueve
 * durante la rueda. Con tolerancia cero casi todo saldría "parcial" y la alerta
 * dejaría de servir. Este número se calibra con las primeras ruedas reales.
 */
export const TOLERANCIA_CONCILIACION = 0.01;

/** Margen absoluto admitido para un monto pedido dado. */
export const toleranciaDe = (monto) =>
  Math.max(1, Math.abs(numeroDe(monto)) * TOLERANCIA_CONCILIACION);

/**
 * Margen para reconocer una caución cerrada a mano por el monto de una orden.
 * Es estricto a propósito: el modal "Monto operado" sólo redondea hacia arriba
 * al entero, y con un margen amplio dos operaciones distintas de montos
 * parecidos se confundirían.
 */
const TOLERANCIA_YA_CARGADA = 1;

/**
 * Lee un campo numérico de una caución tolerando el string vacío.
 *
 * Las cauciones precargadas por el cierre nocturno guardan monto, tasa y días
 * como `""`, que no es número y rompe `isValidCaucion` al actualizar.
 */
export const numeroDe = (valor) => {
  if (valor === '' || valor === null || valor === undefined) return 0;
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

const claveDeCaucion = (c) => `${c.nroComitente}|${c.moneda}|${c.operacion}`;

const milisDe = (createdAt) => {
  if (!createdAt) return 0;
  if (typeof createdAt.toDate === 'function') return createdAt.toDate().getTime();
  if (createdAt instanceof Date) return createdAt.getTime();
  const n = new Date(createdAt).getTime();
  return Number.isFinite(n) ? n : 0;
};

/** Promedio de tasas ponderado por capital, igual que en ParcialesModal. */
export const tasaPonderadaDe = (partidas) => {
  const total = partidas.reduce((acc, p) => acc + numeroDe(p.capital), 0);
  if (total <= 0) return 0;
  const suma = partidas.reduce((acc, p) => acc + numeroDe(p.capital) * numeroDe(p.tasa), 0);
  return suma / total;
};

const clasificar = ({ capitalOperado, montoPedido }) => {
  if (capitalOperado <= 0) return ESTADO_NO_OPERADA;
  // Precargada o caución sin monto definido: lo ejecutado pasa a ser el monto.
  if (montoPedido <= 0) return ESTADO_COMPLETA;
  const tolerancia = toleranciaDe(montoPedido);
  if (capitalOperado > montoPedido + tolerancia) return ESTADO_EXCEDIDA;
  if (capitalOperado < montoPedido - tolerancia) return ESTADO_PARCIAL;
  return ESTADO_COMPLETA;
};

/**
 * Fija cada partida que ya se importó a la caución a la que se imputó antes.
 *
 * Sin esto, resubir el archivo deshacía la importación anterior: la caución ya
 * quedó "Hecha", deja de ser candidata, la partida cae en "sin imputar" y al
 * guardar se le borraba el parcial. Con el listado de órdenes resubir es lo
 * normal (se vuelve a subir a medida que se ejecutan las órdenes).
 *
 * @param {object[]} partidas - las del archivo
 * @param {object[]} previas - docs de `caucionesImportadas` (mercado, idExterno, caucionId)
 * @param {object[]} cauciones - cauciones de la planilla
 * @returns {Record<string,string>} idExterno -> caucionId, para usar como `asignaciones`
 */
export const asignacionesDeImportadas = (partidas = [], previas = [], cauciones = []) => {
  const vigentes = new Set(
    cauciones.filter((c) => ESTADOS_VIGENTES.includes(c.estado)).map((c) => c.id)
  );
  // Las "ya cargadas" no se fijan: fijarlas las mandaría a imputar (y a
  // escribirle un parcial) a la caución cerrada a mano. Se vuelven a reconocer
  // por monto en cada conciliación.
  const caucionPorPartida = new Map(
    previas
      .filter((previa) => vigentes.has(previa.caucionId))
      .filter((previa) => previa.estadoConciliacion !== CONCILIACION_YA_CARGADA)
      .map((previa) => [`${previa.mercado}|${previa.idExterno}`, previa.caucionId])
  );
  return partidas.reduce((asignaciones, partida) => {
    const caucionId = caucionPorPartida.get(`${partida.mercado}|${partida.idExterno}`);
    return caucionId ? { ...asignaciones, [partida.idExterno]: caucionId } : asignaciones;
  }, {});
};

/**
 * @param {object[]} partidas - salida de los parsers, ya con nroComitente resuelto
 * @param {object[]} cauciones - cauciones de la planilla (todas; se filtran acá)
 * @param {Record<string,string>} asignaciones - override manual idExterno -> caucionId ('' para ignorar)
 * @returns {{ porCaucion: object[], sinImputar: object[], noOperadas: object[], yaCargadas: object[], resumen: object }}
 */
export const conciliarEjecuciones = ({ partidas = [], cauciones = [], asignaciones = {} } = {}) => {
  const abiertas = cauciones
    .filter((c) => ESTADOS_ABIERTOS.includes(c.estado))
    .sort((a, b) => milisDe(a.createdAt) - milisDe(b.createdAt));

  const candidatasPorClave = new Map();
  abiertas.forEach((c) => {
    const clave = claveDeCaucion(c);
    if (!candidatasPorClave.has(clave)) candidatasPorClave.set(clave, []);
    candidatasPorClave.get(clave).push(c);
  });

  const porId = new Map(cauciones.map((c) => [c.id, c]));

  // Cauciones que el trader cerró a mano (modal "Monto operado" o parciales
  // cargados a mano) sin pasar por la importación. El importador no tiene
  // registro de esas órdenes y, como el archivo trae todas las del día, las
  // mandaba al pedido abierto que quedara del mismo cliente: la operación se
  // contaba dos veces en el saldo. Las que reciben partidas fijadas de una
  // importación anterior no cuentan: esas ya las cerró el importador.
  const destinosFijados = new Set(Object.values(asignaciones).filter(Boolean));
  const cerradasAMano = cauciones
    .filter((c) => c.estado === 'Hecha' && numeroDe(c.montoOperado) > 0 && !destinosFijados.has(c.id))
    .sort((a, b) => milisDe(a.createdAt) - milisDe(b.createdAt));
  const reclamadas = new Set();
  const cerradaAManoPara = (partida, clave) =>
    cerradasAMano.find(
      (c) =>
        !reclamadas.has(c.id) &&
        claveDeCaucion(c) === clave &&
        Math.abs(numeroDe(c.montoOperado) - numeroDe(partida.capital)) <= TOLERANCIA_YA_CARGADA
    );

  // Paso 1: a cada partida, su caución (default o elección del trader) o su motivo.
  const asignadas = [];
  const sinImputarPlano = [];
  const yaCargadas = [];

  partidas.forEach((partida) => {
    const clave = `${partida.nroComitente}|${partida.moneda}|${partida.operacion}`;
    const candidatos = candidatasPorClave.get(clave) || [];
    const override = asignaciones[partida.idExterno];

    let motivo = null;
    if (partida.esCarteraPropia) motivo = MOTIVO_CARTERA_PROPIA;
    else if (!partida.nroComitente) motivo = MOTIVO_SIN_COMITENTE;

    // El override manda salvo cuando el trader eligió explícitamente ignorarla.
    if (override) {
      const caucion = porId.get(override);
      if (caucion) {
        asignadas.push({ ...partida, caucionId: override, candidatos, elegidaAMano: true });
        return;
      }
    }
    if (override === '') {
      sinImputarPlano.push({ ...partida, candidatos, motivo: motivo || MOTIVO_SIN_PEDIDO, ignoradaAMano: true });
      return;
    }

    // Va antes que los pedidos abiertos: si ya hay una caución cerrada a mano
    // por este monto, el pedido abierto es otro y esta orden no le corresponde.
    const yaCargadaEn = motivo ? null : cerradaAManoPara(partida, clave);
    if (yaCargadaEn) {
      reclamadas.add(yaCargadaEn.id);
      yaCargadas.push({ ...partida, caucionId: yaCargadaEn.id, caucion: yaCargadaEn, candidatos });
      return;
    }

    if (motivo || candidatos.length === 0) {
      sinImputarPlano.push({
        ...partida,
        candidatos,
        motivo: motivo || MOTIVO_SIN_PEDIDO,
      });
      return;
    }

    // Sin spillover automático: todas las partidas de una clave van a la caución
    // más antigua. Si hay más de una candidata queda marcado para que el trader
    // decida, en vez de que el sistema reparta plata entre pedidos distintos.
    asignadas.push({ ...partida, caucionId: candidatos[0].id, candidatos, elegidaAMano: false });
  });

  // Paso 2: agrupar por caución y clasificar.
  const grupos = new Map();
  asignadas.forEach((partida) => {
    if (!grupos.has(partida.caucionId)) grupos.set(partida.caucionId, []);
    grupos.get(partida.caucionId).push(partida);
  });

  const porCaucion = [];
  grupos.forEach((partidasDeLaCaucion, caucionId) => {
    const caucion = porId.get(caucionId);
    if (!caucion) return;

    const capitalOperado = partidasDeLaCaucion.reduce((acc, p) => acc + numeroDe(p.capital), 0);
    const montoPedido = numeroDe(caucion.monto);
    // Lo que pidieron las órdenes. Para una precargada (sin monto) es la única
    // referencia: si no, una orden a medio ejecutar la daría por completa y le
    // dejaría como monto lo ejecutado hasta ese momento.
    const pedidoOrdenes = partidasDeLaCaucion.reduce(
      (acc, p) => acc + Math.max(numeroDe(p.cantidadOrden), numeroDe(p.capital)),
      0
    );
    const tasaPonderada = tasaPonderadaDe(partidasDeLaCaucion);
    const diasEjecutados =
      partidasDeLaCaucion.map((p) => numeroDe(p.dias)).find((d) => d > 0) || numeroDe(caucion.dias);
    const mercados = [...new Set(partidasDeLaCaucion.map((p) => p.mercado))];

    porCaucion.push({
      caucion,
      partidas: partidasDeLaCaucion,
      capitalOperado,
      montoPedido,
      pedidoOrdenes,
      diferencia: capitalOperado - montoPedido,
      tasaPonderada,
      diasEjecutados,
      estadoConciliacion: clasificar({ capitalOperado, montoPedido: montoPedido || pedidoOrdenes }),
      // Un pedido cargado para un mercado puede terminar ejecutándose en otro.
      mercadoDistinto: mercados.length > 0 && !mercados.includes(caucion.mercado),
      mercados,
      variosCandidatos: (partidasDeLaCaucion[0]?.candidatos || []).length > 1,
    });
  });

  // Paso 3: las cauciones abiertas que no recibieron nada.
  const conImputacion = new Set(porCaucion.map((g) => g.caucion.id));
  const noOperadas = abiertas.filter((c) => !conImputacion.has(c.id));

  // Paso 4: agrupar lo no imputado para mostrarlo junto.
  const sinImputarPorClave = new Map();
  sinImputarPlano.forEach((partida) => {
    const clave = `${partida.motivo}|${partida.nroComitente || partida.cuit}|${partida.moneda}|${partida.operacion}`;
    if (!sinImputarPorClave.has(clave)) {
      sinImputarPorClave.set(clave, {
        clave,
        motivo: partida.motivo,
        nroComitente: partida.nroComitente,
        cuit: partida.cuit,
        moneda: partida.moneda,
        operacion: partida.operacion,
        mercado: partida.mercado,
        partidas: [],
      });
    }
    sinImputarPorClave.get(clave).partidas.push(partida);
  });

  const sinImputar = [...sinImputarPorClave.values()].map((grupo) => ({
    ...grupo,
    capitalTotal: grupo.partidas.reduce((acc, p) => acc + numeroDe(p.capital), 0),
    tasaPonderada: tasaPonderadaDe(grupo.partidas),
    dias: grupo.partidas.map((p) => numeroDe(p.dias)).find((d) => d > 0) || 0,
  }));

  const cuenta = (estado) => porCaucion.filter((g) => g.estadoConciliacion === estado).length;
  const cuentaMotivo = (motivo) => sinImputar.filter((g) => g.motivo === motivo).length;

  return {
    porCaucion,
    sinImputar,
    noOperadas,
    yaCargadas,
    resumen: {
      yaCargadas: yaCargadas.length,
      partidasLeidas: partidas.length,
      partidasImputadas: asignadas.length,
      caucionesAfectadas: porCaucion.length,
      completas: cuenta(ESTADO_COMPLETA),
      parciales: cuenta(ESTADO_PARCIAL),
      excedidas: cuenta(ESTADO_EXCEDIDA),
      noOperadas: noOperadas.length,
      sinPedido: cuentaMotivo(MOTIVO_SIN_PEDIDO),
      carteraPropia: cuentaMotivo(MOTIVO_CARTERA_PROPIA),
      sinComitente: cuentaMotivo(MOTIVO_SIN_COMITENTE),
    },
  };
};

/**
 * Arma el update de la caución a partir de su grupo conciliado.
 *
 * Sanea monto, tasa y días porque las precargadas los traen como `""` y eso
 * viola `isValidCaucion` (deben ser number). Si la caución no tenía monto
 * definido, pasa a ser lo que pidieron las órdenes, que con ejecuciones
 * terminadas coincide con lo ejecutado.
 *
 * `montoOperado` va sin redondear a propósito: la Cloud Function
 * `updateCaucionOnParcialChange` recalcula ese campo como la suma cruda de los
 * parciales, así que redondear acá haría que el valor cambie solo después.
 */
export const updateDeCaucion = (grupo, { userName }) => {
  const {
    caucion, capitalOperado, montoPedido, pedidoOrdenes, tasaPonderada, diasEjecutados, estadoConciliacion,
  } = grupo;
  return {
    estado: estadoConciliacion === ESTADO_PARCIAL ? 'En Proceso' : 'Hecha',
    operador: userName,
    montoOperado: capitalOperado,
    tasaOperada: tasaPonderada,
    monto: montoPedido > 0 ? montoPedido : pedidoOrdenes || capitalOperado,
    tasa: numeroDe(caucion.tasa) || tasaPonderada,
    dias: numeroDe(caucion.dias) || diasEjecutados,
  };
};
